import axios from "axios";
import { Button } from "../../../components/core/basic/Button";
import { Modal } from "../../../components/core/basic/Modal";
import CustomHref from "../../../components/core/blocks/CustomHref";
import Invoice from "../../../modules/billing/components/Invoice";
import PaymentInformation from "../../../modules/billing/components/PaymentInformation";
import { StripeElements } from "../../../modules/billing/components/StripeElements";
import SubscriptionPlan from "../../../modules/billing/components/SubscriptionPlan";
import TermsOfService from "../../../components/custom/instructions/TermsOfService";
import React from "react";
import { withTranslation, Trans } from "react-i18next";
import { connect } from "react-redux";
import { createFileRoute } from "@tanstack/react-router";
import { withRouter } from "../../../hocs/withRouter";
import { refreshAmzOnboardStatus, updateAmzOnboardingStatus } from "../../../redux/reducers/amzOnboardReducer";
import { setCurrentUser } from "../../../redux/reducers/userReducer";
import { setErrors } from "../../../redux/reducers/errorReducers";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import checkModule from "../../../utils/checkModule";
import * as Sentry from "@sentry/react";
import { selectModules } from "../../../redux/selectors/modules";
import { SimpleCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import { css } from "../../../../styled-system/css";
import { Grid, VStack } from "../../../../styled-system/jsx";

const URL_PREFIX = getURLPrefix();

class Billing extends React.Component {
  constructor() {
    super();
    this.state = {
      customer: "",
      cancelModal: false,
      exitMessage: "",
      cancelTerms: false,
      subscriptionCancelled: false,
      primaryOrg: false,
      monthlyShelfFee: 0,
      currentNumOfShelves: 0,
    };
    this.closeCancelTerms = this.closeCancelTerms.bind(this);
    this.getShelfPrice = this.getShelfPrice.bind(this);
  }

  componentDidMount() {
    axios.patch(URL_PREFIX + "/api/accounts/").then((res) => {
      if (res.status === 200) {
        this.props.setCurrentUser({
          current_profile: res.data.current_profile,
        });
      }
      this.setState({ primaryOrg: true });
    });

    if (
      this.props.amz_onboard.org_status === "trial_active" ||
      this.props.amz_onboard.org_status === "trial_expired" ||
      this.props.amz_onboard.org_status === "pre_trial"
    ) {
      this.props.navigate({ to: "/user/payment" });
    } else {
      this.getCustomer();
      refreshAmzOnboardStatus(this.props.updateAmzOnboardingStatus, this.props.setErrors);
    }

    if (checkModule(this.props.modules, "SEARCH_ANALYTICS")) {
      this.getShelfPrice();
    }
  }

  getCustomer() {
    const AuthStr = "Bearer ".concat(this.props.auth.tokens.access_token);
    axios
      .get(URL_PREFIX + "/api/billing/customer", {
        params: {},
        headers: { Authorization: AuthStr },
      })
      .then((res) => {
        if (res.data.customer !== undefined) {
          this.setState({ customer: res.data.customer });
        }
      })
      .catch((error) => {
        if (error?.response?.status !== 400) {
          Sentry.captureException(error);
        }
      });
  }

  onCancelClick = async (event) => {
    event.preventDefault();
    axios
      .get(URL_PREFIX + "/api/billing/customer", {
        params: {},
      })
      .then((result) => {
        this.cancelSubscription(result.data.id, result.data.customer);
      })
      .catch((error) => {
        if (error?.response?.status !== 400) {
          Sentry.captureException(error);
        }
      });
  };

  cancelSubscription(subscriptionId, customerId) {
    const subscriptionInfo = {
      subscriptionId: subscriptionId,
      customerId: customerId,
      reason: "Deactivated by Trellis staff",
    };
    axios
      .delete(
        URL_PREFIX + "/api/billing/subscription",
        { data: subscriptionInfo },
        {
          params: {},
        },
      )
      .then(() => {
        this.setState({ cancelModal: false, subscriptionCancelled: true });
      });
  }

  closeCancelTerms() {
    this.setState({ cancelTerms: false });
  }

  getShelfPrice() {
    axios.get(URL_PREFIX + "/api/billing/digitalshelf").then((res) => {
      this.setState({
        monthlyShelfFee: res.data.price,
        currentNumOfShelves: parseInt(res.data.count),
      });
    });
  }

  render() {
    const { t } = this.props;
    return (
      <>
        <SimpleCan action={"read"} subject={"Billing"}>
          {this.state.primaryOrg && (
            <VStack
              gap="xlarge"
              alignItems="stretch"
              className={css({ maxWidth: "1200px", mx: "auto", py: "xlarge", px: "medium" })}
            >
              <h2 className={css({ fontWeight: "bold" })}>{t(($) => $.onboarding["Billing"])}</h2>

              <Grid gridTemplateColumns={{ base: "1fr", lg: "1fr 300px" }} gap="large">
                <StripeElements>
                  <PaymentInformation customerId={this.state.customer} />
                </StripeElements>
                <SubscriptionPlan
                  customerId={this.state.customer}
                  monthlyShelfFee={this.state.monthlyShelfFee}
                  currentNumOfShelves={this.state.currentNumOfShelves}
                  getShelfPrice={this.getShelfPrice}
                  subscriptionCancelled={this.state.subscriptionCancelled}
                />
              </Grid>

              <Invoice customerId={this.state.customer} />

              {this.props.amz_onboard_org_status !== "disconnected" &&
                this.props.amz_onboard.remainder_days === null && (
                  <div className={css({ alignSelf: "flex-end" })}>
                    <span className={css({ fontSize: "14px" })} onClick={() => this.setState({ cancelModal: true })}>
                      <CustomHref text={t(($) => $.onboarding["Cancel Subscription"])} />
                    </span>
                  </div>
                )}

              <Modal
                isOpen={this.state.cancelModal}
                onOpenChange={(isOpen) => {
                  if (!isOpen) this.setState({ cancelModal: false });
                }}
                title={t(($) => $.onboarding["Sorry to see you go"])}
              >
                <VStack gap="large" alignItems="center" className={css({ padding: "medium", fontSize: "14px" })}>
                  <p className={css({ textAlign: "center", fontSize: "1.25rem", maxWidth: "500px" })}>
                    <Trans
                      i18nKey={($) =>
                        $.onboarding[
                          "Thanks for using Trellis. We are sorry to hear that you want to cancel.<br/><br/>Please reach out to Trellis customer success team to make sure that campaigns get transitioned during the cancellation process."
                        ]
                      }
                      t={t}
                      components={{ br: <br /> }}
                    />
                  </p>
                  <p className={css({ textAlign: "center", maxWidth: "500px" })}>
                    <Trans
                      i18nKey={($) =>
                        $.onboarding[
                          "At sign up you agreed to our cancellation policy, click <policyLink>here</policyLink> to view policy agreement."
                        ]
                      }
                      t={t}
                      components={{
                        policyLink: (
                          <span
                            className={css({ color: "fuchsia", cursor: "pointer" })}
                            onClick={() => this.setState({ cancelTerms: true })}
                          />
                        ),
                      }}
                    />
                  </p>
                  {this.props.user?.is_staff ?
                    <Button onPress={(e) => this.onCancelClick(e)}>
                      {t(($) => $.onboarding["Cancel Subscription"])}
                    </Button>
                  : (
                    this.props.amz_onboard.org_status !== "disconnected" &&
                    this.props.amz_onboard.remainder_days === null
                  ) ?
                    <Button
                      onPress={() => {
                        window.location = "mailto:support@gotrellis.com";
                      }}
                    >
                      {t(($) => $.onboarding["Contact Us To Cancel"])}
                    </Button>
                  : null}
                </VStack>
              </Modal>

              {this.state.cancelTerms && (
                <TermsOfService justTerms="showCancellation" closeTerms={this.closeCancelTerms} />
              )}
            </VStack>
          )}
        </SimpleCan>
        <SimpleCan action={"read"} subject={"Billing"} not={true}>
          <UnauthorizedWarning />
        </SimpleCan>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  user: state.user,
  amz_onboard: state.amz_onboard,
  modules: selectModules(state),
});

const mapDispatchToProps = {
  updateAmzOnboardingStatus,
  setErrors,
  setCurrentUser,
};

export const Route = createFileRoute("/_authenticated/user/billing")({
  component: connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation(["onboarding"])(Billing))),
  staticData: { application: "Admin" },
});
